import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { first, last } from 'lodash';
import update from 'immutability-helper';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Button } from '@material-ui/core';
import TimePeriodSelector from './TimePeriodSelector';
import StepperButtons from './StepperButtons';
import CloudSelector from './CloudSelector';
import DateInput from './DateInput';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import ImageTable from '../visualization/ImageTable';
import { getImageKey } from '../visualization/imageCsv';
import ImportDatesButton from './ImportDatesButton';
import AttentionMessage from '../core/AttentionMessage';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import { Actions as Csqueeze } from '../../../store/ducks/csqueeze';
import { Actions as EUSCoast } from '../../../store/ducks/euscoast';
import { Actions as Imagery } from '../../../store/ducks/imagery';
import { FINALIZE, NEXT } from '../../../hooks/useStepNavigations';
import {
  toISOString,
  toTimestamp,
  datesBetween,
  formatDate,
  formatDateDiff,
  useLocalStorage,
} from '../../../common/utils';
import { uniteMissionsDates } from '../../../common/algorithms';
import ReactGA from 'react-ga';
import TourGuider from '../tour/TourGuider';
import moment from 'moment';
import HelpButton from '../core/HelpButton';
import { useErrorNotice } from '../../../hooks/useErrorNotice';

/**
 * The modules whose image step confirms one selection per period into their own store, rather than
 * leaving the chosen images in `availableDates`.
 */
const PERIOD_MODULES = ['csqueeze', 'euscoast'];

/**
 * @function samePeriod
 * @description Whether two image selections are the same set of images, whatever order they are in.
 * @param {Array<Object>} a - One selection.
 * @param {Array<Object>} b - The other.
 * **Called by:
 * @see {PeriodImageChooser} - to avoid confirming a period that is already there.
 * @memberof module:Shared
 * @returns {Boolean} True when both hold exactly the same images.
 */
const samePeriod = (a, b) => {
  if (!a || !b || a.length !== b.length || a.length === 0) return false;
  const keys = new Set(a.map(getImageKey));
  return b.every((image) => keys.has(getImageKey(image)));
};

const normalizePeriod = (start, end) =>
  moment(end).isBefore(moment(start)) ? { start: end, end: start } : { start, end };

const readStoredImageList = (key) => {
  try {
    return JSON.parse(window.sessionStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

/**
 * @function PeriodImageChooser
 * @description Merges the period/cloud search and the resulting image-selection table into a
 * single step, so the user can adjust the period or cloud filter and see the candidate list
 * update immediately, without navigating to a separate step to see it and back again to redo
 * the search.
 * @param {Object} props - Component props.
 * @param {Function} props.navigate - Function to navigate between steps.
 * @param {Number} [props.periodIndex] - Which period this step confirms, for the modules that
 *  confirm one period per step (`PERIOD_MODULES`). Its two image steps are this same component, so
 *  without it neither step can tell which slot of the module's `imageList` it owns and the writes
 *  collide: confirming list 2 overwrote or duplicated list 1. Ignored by the modules that keep
 *  their whole selection in `availableDates`.
 * **Called by:
 * @see {Shoreline} - shoreline's acquisition
 * @see {ShoreRAcquisitionPage} - ShoreR's acquisition
 * @memberof module:Shared
 * @returns {JSX.Element} The PeriodImageChooser component.
 */
const PeriodImageChooser = ({
  navigate,
  sessionImportButton,
  module = 'shoreline',
  variant = 'classic',
  periodIndex = 0,
}) => {
  const isPeriodModule = PERIOD_MODULES.includes(module);
  const dates = useSelector((state) => state.acquisition.availableDates, shallowEqual);
  const datesVersion = useSelector((state) => state.acquisition.availableDatesVersion);
  const missions = useSelector((state) => state.acquisition.missions, shallowEqual);
  const metadata = useSelector((state) => state.acquisition.metadata);
  const savedPeriod = useSelector(
    (state) => ({ start: state.acquisition.start, end: state.acquisition.end }),
    shallowEqual,
  );
  const coordinates = useSelector((state) => state.acquisition.coordinates, shallowEqual);
  const satelliteName = window.sessionStorage.getItem('selectedSatellite');
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const notifyError = useErrorNotice();
  const classes = useStyles();
  const [cloudCoverage, setcloudCoverage] = useState(() => {
    const saved = Number(window.sessionStorage.getItem('acquisitionCloudCoverage'));
    return Number.isFinite(saved) && saved > 0 ? saved : 0.05;
  });
  const [period, setPeriodRaw] = useState(() =>
    savedPeriod.start && savedPeriod.end
      ? normalizePeriod(savedPeriod.start, savedPeriod.end)
      : normalizePeriod(moment().subtract(1, 'year').toISOString(), moment().toISOString()),
  );
  const setPeriod = ({ start, end }) => setPeriodRaw(normalizePeriod(start, end));
  const [searched, setSearched] = useState(Boolean(dates) && dates.length > 0);
  const hasResults = searched && Boolean(dates) && dates.length > 0;
  const [isTourOpen, setIsTourOpen] = useLocalStorage('showPeriodImageTour', true);
  const [isSearching, setIsSearching] = useState(false);
  const skipNextAutoPeriodRef = useRef(Boolean(savedPeriod.start && savedPeriod.end));
  const datesSignatureRef = useRef(null);
  const narrowedSignatureRef = useRef(null);
  const [selected, setSelected] = useState({});

  /**
   * The selection the result page was built from, as this step finds it on opening: coming back
   * from the result, `availableDates` still holds the images confirmed last time. A search replaces
   * it, so it is kept from the first render.
   */
  const confirmedOnArrivalRef = useRef(dates);
  const confirmedPeriods = useSelector((state) => (isPeriodModule ? state[module].imageList : null));
  const ownConfirmedPeriod = useMemo(
    () => (isPeriodModule ? (confirmedPeriods || [])[periodIndex] || null : null),
    [isPeriodModule, confirmedPeriods, periodIndex],
  );
  const confirmedKeys = useMemo(() => {
    const keys = new Set();
    (ownConfirmedPeriod || []).forEach((image) => keys.add(getImageKey(image)));
    return keys;
  }, [ownConfirmedPeriod]);
  const selectionStorageKey = isPeriodModule
    ? `acquisitionImageSelection:${module}:${periodIndex}`
    : 'acquisitionImageSelection';
  const restoredCandidatesRef = useRef(false);
  const fullListStorageKey = isPeriodModule
    ? `acquisitionFullImageList:${module}:${periodIndex}`
    : 'acquisitionFullImageList';
  const fullImageList = useMemo(
    () =>
      readStoredImageList(fullListStorageKey) ||
      (ownConfirmedPeriod?.length ? readStoredImageList('acquisitionFullImageList') : null),
    [fullListStorageKey, ownConfirmedPeriod],
  );
  const canReloadFullList = !isPeriodModule && (fullImageList?.dates?.length ?? 0) > (dates?.length ?? 0);

  const onChange = (newStart, newEnd) => {
    if (moment(newEnd).isBefore(moment(newStart))) {
      notifyError(t('forms.acquisition.3.warning'));
      setPeriod({ start: newStart, end: newStart });
      return;
    }
    setPeriod({ start: newStart, end: newEnd });
  };

  useEffect(() => {
    setIsSearching(false);
  }, [datesVersion]);

  useEffect(() => {
    if (isSearching) setIsSearching(false);
    if (!dates || dates?.length === 0) return;
    const signature = dates.map((d) => `${d.date}-${d.name}`).join('|');
    if (signature === datesSignatureRef.current) return;
    datesSignatureRef.current = signature;
    if (skipNextAutoPeriodRef.current) {
      skipNextAutoPeriodRef.current = false;
      return;
    }
    const sorted = [...dates].sort((a, b) => new Date(a.date) - new Date(b.date));
    setPeriod({ start: first(sorted).date, end: last(sorted).date });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  useEffect(() => {
    window.sessionStorage.setItem('acquisitionCloudCoverage', cloudCoverage);
  }, [cloudCoverage]);

  useEffect(() => {
    if (!PERIOD_MODULES.includes(module) || restoredCandidatesRef.current) return;
    if (dates?.length) {
      restoredCandidatesRef.current = true;
      return;
    }
    if (!fullImageList?.dates?.length) return;
    restoredCandidatesRef.current = true;
    skipNextAutoPeriodRef.current = false;
    setcloudCoverage(1);
    setSearched(true);
    dispatch(Acquisition.setAvailableDates(fullImageList.dates, fullImageList.missions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, dates]);

  useEffect(() => {
    if (!dates || dates?.length === 0) return;
    dispatch(Acquisition.setPeriod(period.start, period.end));
  }, [period, dispatch]);

  /**
   * @function handleSearch
   * @description Searches for available images only within the currently selected start/end
   * period, instead of loading the satellite's entire history upfront. Skips the actual Earth
   * Engine query when the period and AOI are identical to the last successful search and results
   * are already on hand — re-querying for the exact same input would just cost loading time for
   * an identical outcome (e.g. clicking "search" again without having changed anything).
   * @returns {void}
   */
  const handleSearch = () => {
    const signature = JSON.stringify({ start: period.start, end: period.end, coordinates });
    if (
      signature === window.sessionStorage.getItem('acquisitionLastSearchSignature') &&
      Array.isArray(dates) &&
      dates.length > 0
    ) {
      setSearched(true);
      return;
    }
    window.sessionStorage.setItem('acquisitionLastSearchSignature', signature);
    setIsSearching(true);
    setSearched(true);
    dispatch(Acquisition.loadAvailableImages(period.start, period.end));
  };

  const { visibleDates, filteredOutByCloud, narrowedDates } = useMemo(() => {
    if (!hasResults) return { visibleDates: [], filteredOutByCloud: false, narrowedDates: [] };
    const visible = uniteMissionsDates(missions).filter((entry) => entry.content <= cloudCoverage);
    if (visible.length === 0) return { visibleDates: visible, filteredOutByCloud: true, narrowedDates: [] };
    let begin = new Date(period.start);
    begin.setDate(begin.getDate() - 1);
    begin = toISOString(toTimestamp(begin));
    let finish = new Date(period.end);
    finish.setDate(finish.getDate() + 1);
    finish = toISOString(toTimestamp(finish));
    const narrowed = visible.filter((entry) => entry.date >= begin && entry.date <= finish).reverse();
    return { visibleDates: visible, filteredOutByCloud: false, narrowedDates: narrowed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResults, missions, cloudCoverage, period.start, period.end]);

  const visibleDateValues = visibleDates.map((entry) => entry.date).sort();
  const clampToVisible = (date) => {
    if (!visibleDateValues.length || !date) return date;
    if (new Date(date) < new Date(visibleDateValues[0])) return visibleDateValues[0];
    if (new Date(date) > new Date(visibleDateValues[visibleDateValues.length - 1]))
      return visibleDateValues[visibleDateValues.length - 1];
    return date;
  };
  const sliderStart = clampToVisible(period.start);
  const sliderEnd = clampToVisible(period.end);

  useEffect(() => {
    if (!narrowedDates.length) return;
    const signature = narrowedDates.map(getImageKey).join('|');
    if (signature === narrowedSignatureRef.current) return;
    narrowedSignatureRef.current = signature;
    const saved = JSON.parse(window.sessionStorage.getItem(selectionStorageKey)) || {};
    const initial = {};
    narrowedDates.forEach((img) => {
      const key = getImageKey(img);
      initial[key] = saved[key] ?? confirmedKeys.has(key);
    });
    setSelected(initial);
  }, [narrowedDates, confirmedKeys, selectionStorageKey]);

  useEffect(() => {
    window.sessionStorage.setItem(selectionStorageKey, JSON.stringify(selected));
  }, [selected, selectionStorageKey]);

  useEffect(() => {
    if (!narrowedDates.length) return undefined;
    const handle = setTimeout(() => {
      dispatch(Acquisition.loadThumbnails(narrowedDates));
    }, 400);
    return () => {
      clearTimeout(handle);
      dispatch(Acquisition.cancelThumbnails());
    };
  }, [narrowedDates, dispatch]);

  const handleCheckboxChange = (key, checked) => {
    setSelected((prev) => update(prev, { [key]: { $set: checked } }));
  };

  const handleSelectAll = (keys, checked) => {
    setSelected((prev) => {
      const changes = keys.reduce((acc, key) => {
        acc[key] = { $set: checked };
        return acc;
      }, {});
      return update(prev, changes);
    });
  };

  /**
   * @function handleReloadFullList
   * @description Restores the full candidate list backed up by handleFinish (see below), so the
   * user can get back every image that matched their original period/cloud filters after having
   * finalized a narrower selection and come back to this step — without redoing the search. Also
   * resets the cloud filter to 100% — otherwise a leftover narrow cloud filter would immediately
   * re-hide part of the restored list. The period widens back out on its own, via the existing
   * "genuinely new dates" effect above (`dates` changing to the backup's wider set). Previously-
   * checked images stay checked (selection is tracked separately, by key).
   * @returns {void}
   */
  const handleReloadFullList = () => {
    try {
      if (module === 'csqueeze' || module === 'euscoast') return;
      const backup = JSON.parse(window.sessionStorage.getItem('acquisitionFullImageList') || 'null');
      if (backup?.dates?.length) {
        setcloudCoverage(1);
        dispatch(Acquisition.setAvailableDates(backup.dates, backup.missions));
        dispatch(Acquisition.loadThumbnails(backup.dates));
      }
    } catch {}
  };

  /**
   * @function handleFinish
   * @description Commits the checked images and advances past this step. Also backs up the
   * full (pre-selection) candidate list to sessionStorage before narrowing it — setAvailableDates
   * below overwrites state.acquisition.availableDates with ONLY the selected subset, so without
   * this backup, coming back to this step later (e.g. from the Result page) would only ever show
   * whatever was selected before, with no way to recover the rest short of searching again. Only
   * overwrites the backup when the current list is at least as large, so repeatedly finalizing an
   * already-narrowed list can't shrink it.
   * On the Shoreline, a selection other than the one the result was built from restarts that
   * analysis: the result picks its steps from the images it kept, so coming back for more images
   * in the same period landed on the baseline with the old images, past the image selection.
   * @returns {void}
   */
  const handleFinish = () => {
    ReactGA.event({ category: 'Acquisition', action: 'SetcloudCoverage', value: cloudCoverage });
    ReactGA.event({ category: 'Acquisition', action: 'SetPeriod', value: period.start + '-' + period.end });
    const backup = JSON.stringify({ dates: narrowedDates, missions });
    const existing = readStoredImageList('acquisitionFullImageList');
    if (!existing || narrowedDates.length >= existing.dates.length) {
      window.sessionStorage.setItem('acquisitionFullImageList', backup);
    }
    if (isPeriodModule) window.sessionStorage.setItem(fullListStorageKey, backup);
    const finalSelection = narrowedDates.filter((img) => selected[getImageKey(img)]);
    if (!isPeriodModule) {
      dispatch(Acquisition.setAvailableDates(finalSelection));
      if (module === 'shoreline' && !samePeriod(confirmedOnArrivalRef.current, finalSelection)) {
        dispatch(Imagery.restartAnalysis());
      }
    } else if (!samePeriod(ownConfirmedPeriod, finalSelection)) {
      if (module === 'csqueeze') dispatch(Csqueeze.setImageListAt(periodIndex, finalSelection));
      if (module === 'euscoast') dispatch(EUSCoast.setImageListAt(periodIndex, finalSelection));
    }
    if (module !== 'shoreline') dispatch(Acquisition.setAvailableDates([]));
  };

  const steps = [
    { selector: '#timeselector', content: t('tour.acquisition.3.period') },
    { selector: '#timeselector', content: t('tour.acquisition.3.point') },
    { selector: '#cloudselector', content: t('tour.acquisition.3.cloud') },
  ];
  if (hasResults && !filteredOutByCloud) {
    steps.push(
      { selector: '#column0', content: t('tour.acquisition.4.id') },
      { selector: '#column1', content: t('tour.acquisition.4.clouds') },
      { selector: '#column2', content: t('tour.acquisition.4.image') },
      { selector: '#columnaction', content: t('tour.acquisition.4.action') },
    );
  }

  return (
    <Box>
      <HelpButton
        onClickFunction={() => {
          setIsTourOpen(true);
        }}
        extra={sessionImportButton}
      />
      <ActivityIndicator title={isSearching ? t('forms.core.loading.searchingImages') : undefined} />
      <Box className={classes.wrapper} display="flex" alignItems="center" flexDirection="column">
        <Box className={classes.dateInputs} display="flex" gap={2} mb={2}>
          <DateInput
            className={classes.dateField}
            label={t('forms.acquisition.3.startDate')}
            value={period.start}
            onBlur={(newStart) => onChange(newStart, period.end)}
            charLanguage={t('forms.transectEvolution.chartLanguage')}
            showCalendarPicker
          />
          <DateInput
            className={classes.dateField}
            label={t('forms.acquisition.3.endDate')}
            value={period.end}
            onBlur={(newEnd) => onChange(period.start, newEnd)}
            charLanguage={t('forms.transectEvolution.chartLanguage')}
            showCalendarPicker
          />
        </Box>
        <CloudSelector level={cloudCoverage} onChange={(cloudCoverage) => setcloudCoverage(cloudCoverage)} />
        <Button
          className={classes.margin}
          variant="contained"
          color="primary"
          disabled={isSearching}
          onClick={handleSearch}>
          {t('forms.acquisition.3.searchImages')}
        </Button>
        {searched && !isSearching && !hasResults && (
          <Typography variant="subtitle1" align="center" className={classes.description}>
            {t('forms.acquisition.3.noImagesFound')}
          </Typography>
        )}
        {filteredOutByCloud && !isSearching && (
          <Typography variant="subtitle1" align="center" className={classes.description}>
            {t('forms.acquisition.3.noImagesMatchCloudFilter')}
          </Typography>
        )}
        {hasResults && !isSearching && !filteredOutByCloud && (
          <>
            <TimePeriodSelector
              key={visibleDates.map((entry) => entry.date).join('|')}
              dates={visibleDates.map((entry) => entry.date)}
              start={sliderStart}
              end={sliderEnd}
              onChange={(start, end) => onChange(start, end)}
            />
            <Box className={classes.description}>
              <Typography variant="subtitle1" align="center">
                {t('forms.acquisition.3.period')}: {formatDate(period.start)} {t('forms.acquisition.3.to')}{' '}
                {formatDate(period.end)}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {formatDateDiff(period.start, period.end)},{' '}
                {
                  datesBetween(
                    visibleDates.map((entry) => entry.date),
                    period.start,
                    period.end,
                  ).length
                }{' '}
                {t(
                  `forms.acquisition.3.imageQuantity.${
                    datesBetween(
                      visibleDates.map((entry) => entry.date),
                      period.start,
                      period.end,
                    ).length === 1
                      ? 'singular'
                      : 'plural'
                  }`,
                )}
              </Typography>
            </Box>
            {satelliteName === 'Landsat' && (
              <AttentionMessage
                title={t('forms.acquisition.4.attention')}
                message={t('forms.acquisition.4.attentionMessage')}
              />
            )}
            {canReloadFullList && (
              <Box mb={2} display="flex" justifyContent="center">
                <Button variant="outlined" color="primary" onClick={handleReloadFullList}>
                  {t('forms.acquisition.4.reloadFullList')}
                </Button>
              </Box>
            )}
            <Box mb={1}>
              <ImportDatesButton images={narrowedDates} onImport={setSelected} />
            </Box>
            <Box width="100%">
              <ImageTable
                metadata={metadata}
                images={narrowedDates}
                selected={selected}
                onCheckboxChange={handleCheckboxChange}
                onSelectAll={handleSelectAll}
                variant={variant}
              />
            </Box>
          </>
        )}
        <StepperButtons
          onBack={() => {
            if (module === 'csqueeze') dispatch(Csqueeze.truncateImageList(periodIndex));
            if (module === 'euscoast') dispatch(EUSCoast.truncateImageList(periodIndex));
          }}
          navigate={navigate}
          nextTarget={module === 'shoreline' ? FINALIZE : NEXT}
          onNext={handleFinish}
          nextDisabled={!hasResults || filteredOutByCloud}
        />
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: theme.spacing(2),
  },
  description: {
    margin: theme.spacing(7, 0, 3),
  },
  margin: {
    margin: '5px',
  },
}));

export default PeriodImageChooser;
